import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, LogOut, LayoutDashboard } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_research-hub-218/artifacts/lk2wm99x_Skoop%21%20Logo%20-%202026.jpg";

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/services', label: 'Services' },
  { to: '/get-the-skoop', label: 'Get the SKOOP!' },
  { to: '/contact', label: 'Contact Us' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <nav data-testid="navbar" className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b-2 border-[#0A192F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="SKOOP! Logo" className="h-12 sm:h-16 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-4 py-2 rounded-lg font-semibold text-sm tracking-wide transition-colors ${
                  location.pathname === link.to
                    ? 'bg-[#0A192F] text-white'
                    : 'text-[#0A192F] hover:bg-[#0A192F]/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {user && user.role === 'admin' ? (
              <>
                <Link
                  to="/admin"
                  data-testid="nav-admin-dashboard"
                  className="neo-btn px-4 py-2 bg-[#166534] text-white text-sm flex items-center gap-2"
                >
                  <LayoutDashboard size={16} /> Dashboard
                </Link>
                <button
                  onClick={logout}
                  data-testid="nav-logout-btn"
                  className="neo-btn px-4 py-2 bg-[#DC2626] text-white text-sm flex items-center gap-2"
                >
                  <LogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <Link
                to="/#signup"
                data-testid="nav-signup-cta"
                className="neo-btn px-5 py-2.5 bg-[#38BDF8] text-[#0A192F] text-sm"
                onClick={() => {
                  if (location.pathname === '/') {
                    const el = document.getElementById('signup');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Join SKOOP!
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 text-[#0A192F]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t-2 border-[#0A192F] bg-white px-4 pb-4">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 rounded-lg font-semibold text-sm ${
                location.pathname === link.to
                  ? 'bg-[#0A192F] text-white'
                  : 'text-[#0A192F]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-3 pt-3 border-t border-gray-200">
            {user && user.role === 'admin' ? (
              <>
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-lg font-semibold text-sm text-[#166534]">
                  Dashboard
                </Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="block w-full text-left px-4 py-3 rounded-lg font-semibold text-sm text-[#DC2626]">
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/#signup"
                onClick={() => {
                  setMobileOpen(false);
                  setTimeout(() => {
                    const el = document.getElementById('signup');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="block neo-btn px-4 py-3 bg-[#38BDF8] text-[#0A192F] text-center text-sm"
              >
                Join SKOOP!
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
